import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';

import { getURLProps } from '@utils/setPagesUrl';

import APIRest from '@api/rest';
import { withSsrCache } from '../../ssrCache';

/** Ответ REST-метода meta.get (rest.js не типизирован) */
export type SeoInfoT = {
  siteTitle?: string;
  siteKeywords?: string;
  siteDescription?: string;
  pageLastModified?: string;
  relCanonical?: string | null;
  pageAmp?: boolean | string;
  'og:type'?: string | boolean | null;
  'og:image'?: string | boolean | null;
  bottomText?: string;
  siteH1?: string | null;
  noindex?: boolean;
  status?: string | number | null;
};

/**
 * URL, который отправляется в meta.get — как раньше в updateCache:
 * для кабинета отрезаем /r/-часть, остальное шлём как есть.
 */
export const prepareSeoRequestUrl = (pathname: string): string => {
  if (pathname.includes('/cabinet/')) {
    return pathname.split('/r/')[0];
  }

  return pathname;
};

/**
 * Ключ кэша меты: база страницы до /r/, для рецептов — плюс значимые
 * фильтры. Ключ меняется ровно в тех случаях, когда раньше срабатывали
 * compareUrlWithoutParams / compareRecipesPage в CacheDataRoute
 * (смена page/search внутри той же страницы мету не перезапрашивает).
 */
export const normalizeSeoUrl = (pathname: string): string => {
  const baseUrl = pathname.split('/r/')[0];

  if (pathname.includes('/recipes/r/')) {
    const { bludo, obrabotka, slozhnost, type, naznachenie } = getURLProps({
      pathname
    });

    return [baseUrl, bludo, obrabotka, slozhnost, type, naznachenie].join('|');
  }

  return baseUrl;
};

/**
 * Фабрика опций — единая для useSeoQuery (клиент) и SSR-префетча
 * (src/ssrPrefetch.ts): ключи и queryFn обязаны совпадать байт-в-байт,
 * иначе серверная мета не заматчится кэшем на гидрации.
 */
export const seoQueryOptions = (pathname: string) =>
  // Профиль 'meta' — 5 минут (решение Sergei): мета меняется чаще
  // справочников, а записей тут столько же, сколько адресов
  withSsrCache(
    queryOptions({
      queryKey: ['seo', normalizeSeoUrl(pathname)],
      queryFn: () =>
        APIRest.getSeoInfo(prepareSeoRequestUrl(pathname)) as Promise<SeoInfoT>,
      placeholderData: keepPreviousData,
      // Ошибка должна сразу давать 404-фолбэк, а rest.js на каждом фейле
      // показывает notification — поэтому без ретраев
      retry: false,
      // Мету незачем перезапрашивать по фокусу вкладки: в живой вкладке
      // могли бы внезапно смениться render:status_code / last_modified
      refetchOnWindowFocus: false
    }),
    'meta'
  );

export const useSeoQuery = (pathname: string) => useQuery(seoQueryOptions(pathname));
